import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { feedbackApi } from '../api/endpoints.ts';

/**
 * The bar across the top of a feature preview (spec §22, §26).
 *
 * Two jobs, and the first one matters more: SAY THIS IS A DEMO. Somebody who
 * arrived here through Try Preview is signed in as an administrator of a
 * company full of realistic-looking customers and invoices, and the single
 * worst outcome of this whole feature is a cleaner believing they are editing
 * their own account. So the notice is permanent, it is at the top of every
 * page, and it says what will happen to what they type: nothing.
 *
 * The second job is the response prompt. Its buttons link back to the
 * PRODUCTION board rather than posting from here, because this tenant is
 * disposable — anything recorded in it disappears with it — and because the
 * person's real session lives over there.
 *
 * Renders nothing anywhere else. The endpoint answers `{ previewing: null }`
 * on any deployment that does not redeem preview tokens and for any visitor
 * with no preview cookie, so the ordinary app pays one cached request for it.
 */
export default function PreviewBanner() {
  const { t } = useTranslation();

  const query = useQuery({
    queryKey: ['feedback-preview-context'],
    queryFn: () => feedbackApi.previewContext(),
    // The answer cannot change within a session: it comes from a cookie set
    // once, at the moment this tenant was created.
    staleTime: Infinity,
    retry: false,
  });

  const previewing = query.data?.previewing;
  if (!previewing) return null;

  return (
    <div className="border-b-4 border-[color:var(--color-warning)] bg-[color:var(--color-warning-soft)] px-4 py-3 text-[color:var(--color-warning)]">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-bold uppercase tracking-wide">{t('feedback.demoBannerTitle')}</p>
          <p className="text-sm">{t('feedback.demoBannerBody')}</p>
          {previewing.title ? (
            <p className="mt-1 text-sm">{t('feedback.demoBannerPreviewing', { title: previewing.title })}</p>
          ) : null}
        </div>

        {previewing.feedbackUrl ? (
          <div className="flex flex-wrap gap-2">
            {/* Straight to the response card on the real board. `noreferrer`
                because this origin's URL is nobody's business over there, and
                `noopener` because a demo tenant must not hold a handle on the
                window it opened. */}
            <a
              className="btn btn-primary"
              href={`${previewing.feedbackUrl}?respond=1`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('feedback.demoBannerLeaveFeedback')}
            </a>
          </div>
        ) : null}
      </div>
    </div>
  );
}
